import { useState, useEffect, useMemo } from 'react'
import { toast } from '@dd3tech/wiggui/Toast'
import { useAmplitude } from '@wiggot/analytics'
import {
  getAssociations,
  getPortalsLightCredentials,
  postPublishProperties,
  postUnPublishProperties
} from '../../../services'
import { useModalPublishOrUnpublishPropertyStore } from '../../../store'
import { createUrlMedia } from '../../../../utils'
import {
  convertToPublishPortals,
  validateAdSelectedOptions
} from '../../../utils'
import useRoles from '@wiggot/auth/hooks/useRoles'
import { delayedPublicationPortals } from '../../../constants'
import type { IPortalLightCredential } from '../../../../interfaces'

const assignedTextsByType = {
  publish: {
    title: 'Publicar',
    type: 'publicar',
    portalDescription:
      'Selecciona los portales en los que quieres publicar esta propiedad. Ten en cuenta que antes de seleccionarlos debes conectar tu cuenta de cada plataforma con Wiggot, puedes hacerlo en la sección de',
    associationsDescription:
      'Selecciona las asociaciones en las que quieres publicar la propiedad.'
  },
  unpublish: {
    title: 'Despublicar',
    type: 'despublicar',
    portalDescription:
      'Des selecciona los portales de los que quieres despublicar esta propiedad.',
    associationsDescription:
      'Des selecciona las asociaciones de las que quieres despublicar la propiedad.'
  }
} as const

export type TabSelected = 'portals' | 'associations'

export const usePublishOrUnpublishProperty = () => {
  const { trackEvent } = useAmplitude()
  const { isAdminAgency, isIndependentAgent, isBasicUser } = useRoles()
  const [disabledConfirmButton, setDisabledConfirmButton] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingPublishOrUnpublish, setIsLoadingPublishOrUnpublish] =
    useState(false)
  const [tabSelected, setTabSelected] = useState<TabSelected>('portals')

  const selectedPortalsIds = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.selectedPortalsIds
  )

  const selectedAdOptions = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.selectedAdOptions
  )

  const modalState = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.modalState
  )

  const availablePortals = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.availablePortals
  )
  const setAvailablePortals = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.setAvailablePortals
  )

  const selectedAssociations = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.selectedAssociations
  )

  const dataAssociations = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.dataAssociations
  )
  const setDataAssociations = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.setDataAssociations
  )

  const onSuccess = modalState?.onSuccess

  const textsByType = assignedTextsByType[modalState?.type]

  const isDisabledByOptionWithoutAd =
    validateAdSelectedOptions(selectedPortalsIds, selectedAdOptions) &&
    (isAdminAgency || isIndependentAgent || isBasicUser) &&
    modalState?.type === 'publish'

  const sortWiggotFirst = (portalId: string, beforePortalId: string) => {
    if (portalId === 'WIGGOT') {
      return -1
    }

    if (beforePortalId === 'WIGGOT') {
      return 1
    }

    if (portalId === 'MONOPOLIO') {
      return beforePortalId === 'WIGGOT' ? 1 : -1
    }

    if (beforePortalId === 'MONOPOLIO') {
      return portalId === 'WIGGOT' ? -1 : 1
    }
    return 0
  }

  const allAssociationsDefault = useMemo(() => {
    return dataAssociations?.length > 0
      ? dataAssociations
          ?.filter((association) => association?.id !== undefined)
          ?.map((association) => {
            return association?.id?.toString()
          })
      : []
  }, [dataAssociations])

  const allAsociationsPropertiesDefaultSelected =
    modalState?.propertiesDetail?.map((property) => {
      return property?.publishedInAssociations
        .map((association) => {
          return association?.id?.toString()
        })
        .filter((association) => association !== undefined)
    })

  const allAssociationsDefaultSelected = allAsociationsPropertiesDefaultSelected
    ?.reduce((acc, associations) => {
      return acc.filter((association) => associations.includes(association))
    })
    ?.filter((association) => association !== undefined)

  const allPortalsPropertiesDefaultSelected = modalState?.propertiesDetail?.map(
    (property) => {
      const publishedPortals = property?.publishedPortalIds ?? []

      const pendingPortals = property?.pendingPortalIds ?? []

      const updatingPortals = property?.updatingPortalIds ?? []

      const allPortals = [
        ...publishedPortals,
        ...pendingPortals,
        ...updatingPortals
      ]

      return allPortals
    }
  )

  const allPortalsDefaultSelected = allPortalsPropertiesDefaultSelected
    ?.reduce((acc, portals) => {
      return acc.filter((portal) => portals.includes(portal))
    })
    ?.filter((portal) => portal)

  const availablePortalsWithAdOptionsAndSort = availablePortals
    ?.map((portal) => {
      const logoUrl = createUrlMedia({
        id: portal?.logo
      })

      const publishIsFailed =
        modalState.type === 'publish' &&
        modalState?.propertiesDetail?.every((property) => {
          return property?.failurePortalIds?.includes(portal?.portalId)
        })

      const checkboxIsCheckedAndDisabled =
        modalState.type === 'publish' &&
        modalState?.propertiesDetail?.every(
          (property) =>
            property?.publishedPortalIds?.includes(portal?.portalId) ||
            property?.pendingPortalIds?.includes(portal?.portalId) ||
            property?.updatingPortalIds?.includes(portal?.portalId) ||
            portal?.portalId === 'WIGGOT' ||
            portal?.portalId === 'MONOPOLIO' ||
            portal?.status
        )

      const isPendingPublish =
        modalState.type === 'publish' &&
        modalState?.propertiesDetail?.every((property) => {
          return property?.pendingPortalIds?.includes(portal?.portalId)
        })

      const isUpdatingPublish =
        modalState.type === 'publish' &&
        modalState?.propertiesDetail?.every((property) => {
          return property?.updatingPortalIds?.includes(portal?.portalId)
        })

      const showDelayedText = delayedPublicationPortals.includes(
        portal?.portalId
      )

      return {
        ...portal,
        checkboxIsCheckedAndDisabled,
        showDelayedText,
        isPendingPublish,
        publishIsFailed,
        isUpdatingPublish,
        img: logoUrl
      }
    })
    .sort((portal, beforePortal) => {
      return sortWiggotFirst(portal?.portalId, beforePortal?.portalId)
    })

  const portals = availablePortalsWithAdOptionsAndSort?.filter(
    (portal) => !portal?.isAssociation
  )
  const associations = availablePortalsWithAdOptionsAndSort?.filter(
    (portal) => portal?.isAssociation
  )

  const optionsByTabSelected =
    tabSelected === 'portals' ? portals : associations

  const filterOptionsUnPublish = optionsByTabSelected?.filter((portal) => {
    const statusPortalAssociation =
      portal?.isAssociation &&
      dataAssociations?.find((association) => {
        return (
          association?.name?.toString()?.replace(/\s+/g, '') ===
          portal?.portalId?.toString().replace(/\s+/g, '')
        )
      })?.status

    return (
      modalState?.propertiesDetail?.every((property) => {
        return (
          property?.publishedPortalIds?.includes(portal?.portalId) ||
          (portal?.isAssociation &&
            property?.publishedInAssociations.find(
              (item) => item.id === portal?.associationId
            )) ||
          (portal?.name &&
            property?.publishedPortalIds?.includes(portal?.name)) ||
          (portal?.id &&
            property?.publishedPortalIds?.includes(portal?.id?.toString()))
        )
      }) && !statusPortalAssociation
    )
  })

  const optionsByTabAndType =
    modalState?.type === 'publish'
      ? optionsByTabSelected
      : filterOptionsUnPublish

  const resetModalState = useModalPublishOrUnpublishPropertyStore(
    (state) => state?.resetModalState
  )

  const onClose = () => {
    resetModalState()
    setTabSelected('portals')
  }

  const fetchAvailablePortals = async () => {
    try {
      setIsLoading(true)
      const { isSuccess, data } = await getPortalsLightCredentials()

      if (isSuccess && data) {
        setAvailablePortals(data)
        fetchAssociations(data)
        return
      }

      toast({
        type: 'error',
        title: 'Ocurrió un error al obtener los portales disponibles'
      })
    } catch {
      toast({
        type: 'error',
        title: 'Ocurrió un error al obtener los portales disponibles'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const generatePublishPortals = useMemo(() => {
    const publishPortals = {
      ...convertToPublishPortals({
        selectedPortals: selectedPortalsIds,
        selectedAssociations,
        selectedAdOptions,
        initialAssociations: allAssociationsDefault.filter(
          (association) => association !== undefined
        ),
        allAssociationsDefaultSelected,
        defaultPortalsSelected: allPortalsDefaultSelected
      })
    }
    return publishPortals
  }, [
    selectedPortalsIds,
    selectedAssociations,
    selectedAdOptions,
    allAssociationsDefault,
    allAssociationsDefaultSelected,
    allPortalsDefaultSelected
  ])

  const fetchPublishProperties = async () => {
    setIsLoadingPublishOrUnpublish(true)
    try {
      const { isSuccess } = await postPublishProperties({
        propertiesIds: modalState?.propertiesIds,
        portals: generatePublishPortals
      })

      if (isSuccess) {
        trackEvent({
          name: 'propertiesPublish_action',
          configs: {
            path: true,
            login: false
          }
        })
        toast({
          type: 'success',
          title:
            modalState?.propertiesIds?.length === 1
              ? 'La propiedad se envió a los portales y/o asociaciones'
              : 'Las propiedades se enviaron a los portales y/o asociaciones'
        })

        onSuccess?.(generatePublishPortals)
        onClose()
        return
      }

      toast({
        type: 'error',
        title:
          modalState?.propertiesIds?.length === 1
            ? 'No se envió a los portales y/o asociaciones'
            : 'No se enviaron a los portales y/o asociaciones'
      })
    } catch {
      toast({
        type: 'error',
        title:
          modalState?.propertiesIds?.length === 1
            ? 'No se envió a los portales y/o asociaciones'
            : 'No se enviaron a los portales y/o asociaciones'
      })
    } finally {
      setIsLoadingPublishOrUnpublish(false)
    }
  }

  const fetchUnpublishProperties = async () => {
    setIsLoadingPublishOrUnpublish(true)
    try {
      const dataUnpublish = {
        associations:
          selectedAssociations?.length > 0
            ? selectedAssociations.map((item) => Number(item))
            : [],
        portalsIds: selectedPortalsIds?.length > 0 ? selectedPortalsIds : [],
        propertiesIds: modalState?.propertiesIds
      }
      const { isSuccess } = await postUnPublishProperties({
        ...dataUnpublish
      })

      if (isSuccess) {
        trackEvent({
          name: 'propertiesUnpublish_action',
          configs: {
            path: true,
            login: false
          }
        })
        toast({
          type: 'success',
          title:
            modalState?.propertiesIds?.length === 1
              ? 'Se despublicó de los portales y/o asociaciones'
              : 'Se despublicaron de los portales y/o asociaciones'
        })

        onSuccess?.(dataUnpublish)
        onClose()
        return
      }

      toast({
        type: 'error',
        title:
          modalState?.propertiesIds?.length === 1
            ? 'No se despublicó de los portales y/o asociaciones'
            : 'No se despublicaron de los portales y/o asociaciones'
      })
    } catch {
      toast({
        type: 'error',
        title:
          modalState?.propertiesIds?.length === 1
            ? 'No se despublicó de los portales y/o asociaciones'
            : 'No se despublicaron de los portales y/o asociaciones'
      })
    } finally {
      setIsLoadingPublishOrUnpublish(false)
    }
  }

  const handleOnClick = () => {
    if (modalState.type === 'publish') {
      fetchPublishProperties()
      return
    }

    fetchUnpublishProperties()
  }

  const fetchAssociations = async (prevPortals: IPortalLightCredential[]) => {
    const propertiesIds = modalState?.propertiesIds?.join(',')

    try {
      const { isSuccess, data } = await getAssociations(propertiesIds)

      if (isSuccess && data) {
        setDataAssociations(data)
        const transformStatusAssociations = prevPortals?.map((portal) => {
          if (portal.isAssociation) {
            const statusAssociation = data?.find(
              (item) => item?.id === portal?.associationId
            )
            return {
              ...portal,
              status: statusAssociation?.status
            }
          }

          return {
            ...portal
          }
        })

        setAvailablePortals([...transformStatusAssociations])
      }
    } catch (error) {
      console.error(error)
    }
  }

  const refetchServices = () => {
    fetchAvailablePortals()
  }

  useEffect(() => {
    if (availablePortals?.length === 0) {
      fetchAvailablePortals()
    }
  }, [])

  return {
    textsByType,
    optionsByTabAndType,
    isLoading,
    isLoadingPublishOrUnpublish,
    tabSelected,
    setTabSelected,
    availablePortals,
    modalState,
    onClose,
    setDisabledConfirmButton,
    disabledConfirmButton,
    handleOnClick,
    isDisabledByOptionWithoutAd,
    refetchServices
  }
}
